import { z } from "zod";

import { ISO_COUNTRY_CODES, isIsoCountryCode } from "./countries.js";

/**
 * The most inventory items one fulfillment order may carry, and so the most one shipping fee
 * covers (boxed's `SHIPPING_MAX_CARDS`).
 */
export const SHIPPING_MAX_ITEMS = 15;

export const SHIPPING_DELIVERY_ESTIMATE_MAX = 100;

/**
 * One row of a shipping config: a country the environment ships to, what a withdrawal order to
 * it costs the shopper in cents, and the delivery-estimate copy the storefront shows beside it.
 * `enabled: false` keeps the row and its cost but takes the country off every shopper-facing list
 * and out of the shippability gate (boxed retires a destination the same way; it has a third
 * "digital only" state Loot OS has no use for). The display name is never stored: `countryDisplayName`
 * derives it at render time.
 */
export const shippingDestinationSchema = z.object({
  countryCode: z
    .string()
    .trim()
    .toUpperCase()
    .refine(isIsoCountryCode, "two-letter ISO country code"),
  enabled: z.boolean(),
  /** Integer cents (currency is presentation, `formatAmount` renders it in the store's units). */
  costCents: z.int().min(0),
  deliveryEstimate: z.string().trim().min(1).max(SHIPPING_DELIVERY_ESTIMATE_MAX),
});
export type ShippingDestination = z.infer<typeof shippingDestinationSchema>;

/**
 * The whole config as it is stored and saved: an ordered list (the order is the storefront's
 * display order) with each country at most once. An empty list is a valid config that ships
 * nowhere.
 */
export const shippingDestinationsSchema = z
  .array(shippingDestinationSchema)
  .max(ISO_COUNTRY_CODES.length)
  .superRefine((destinations, ctx) => {
    const seen = new Set<string>();
    destinations.forEach((destination, index) => {
      if (seen.has(destination.countryCode)) {
        ctx.addIssue({
          code: "custom",
          message: `country ${destination.countryCode} is listed more than once`,
          path: [index, "countryCode"],
        });
      }
      seen.add(destination.countryCode);
    });
  });

/** What a shopper sees: the enabled rows without the switch itself. */
export const publicShippingDestinationSchema = shippingDestinationSchema.omit({ enabled: true });
export type PublicShippingDestination = z.infer<typeof publicShippingDestinationSchema>;

/** The `shipping` block of the resolved storefront config: enabled destinations, in order. */
export const storefrontShippingSchema = z.object({
  destinations: z.array(publicShippingDestinationSchema),
});
export type StorefrontShipping = z.infer<typeof storefrontShippingSchema>;

/** The row for `countryCode` (any state), or undefined. */
export function findShippingDestination<T extends { countryCode: string }>(
  destinations: readonly T[],
  countryCode: string,
): T | undefined {
  const upper = countryCode.toUpperCase();
  return destinations.find((destination) => destination.countryCode === upper);
}

/** The rows a shopper may ship to, in config order. */
export function enabledDestinations(
  destinations: readonly ShippingDestination[],
): ShippingDestination[] {
  return destinations.filter((destination) => destination.enabled);
}

export function toPublicShippingDestination(
  destination: ShippingDestination,
): PublicShippingDestination {
  return {
    countryCode: destination.countryCode,
    costCents: destination.costCents,
    deliveryEstimate: destination.deliveryEstimate,
  };
}
